import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  User, Calendar, ShoppingCart, Package, CreditCard, 
  UserCog, Receipt, ChevronRight, Truck, Banknote
} from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

// Types d'entités supportés
export const ENTITY_TYPES = {
  GUEST: 'guest',
  RESERVATION: 'reservation',
  SALE: 'sale',
  PRODUCT: 'product',
  PAYMENT: 'payment',
  EMPLOYEE: 'employee',
  EXPENSE: 'expense',
  SUPPLIER: 'supplier',
  CASH_MOVEMENT: 'cash_movement'
};

// Configurations par type d'entité
export const entityConfig = {
  [ENTITY_TYPES.GUEST]: {
    icon: User,
    color: 'text-blue-600 hover:text-blue-800',
    bgColor: 'bg-blue-50',
    label: 'Client',
    route: '/guests'
  },
  [ENTITY_TYPES.RESERVATION]: {
    icon: Calendar,
    color: 'text-purple-600 hover:text-purple-800',
    bgColor: 'bg-purple-50',
    label: 'Réservation',
    route: '/reservations'
  },
  [ENTITY_TYPES.SALE]: {
    icon: ShoppingCart,
    color: 'text-green-600 hover:text-green-800',
    bgColor: 'bg-green-50',
    label: 'Vente',
    route: '/sales'
  },
  [ENTITY_TYPES.PRODUCT]: {
    icon: Package,
    color: 'text-orange-600 hover:text-orange-800',
    bgColor: 'bg-orange-50',
    label: 'Produit',
    route: '/products'
  },
  [ENTITY_TYPES.PAYMENT]: {
    icon: CreditCard,
    color: 'text-emerald-600 hover:text-emerald-800',
    bgColor: 'bg-emerald-50',
    label: 'Paiement',
    route: '/payments'
  },
  [ENTITY_TYPES.EMPLOYEE]: {
    icon: UserCog,
    color: 'text-indigo-600 hover:text-indigo-800',
    bgColor: 'bg-indigo-50',
    label: 'Employé',
    route: '/employees'
  },
  [ENTITY_TYPES.EXPENSE]: {
    icon: Receipt,
    color: 'text-red-600 hover:text-red-800',
    bgColor: 'bg-red-50',
    label: 'Dépense',
    route: '/expenses'
  },
  [ENTITY_TYPES.SUPPLIER]: {
    icon: Truck,
    color: 'text-amber-600 hover:text-amber-800',
    bgColor: 'bg-amber-50',
    label: 'Fournisseur',
    route: '/suppliers'
  },
  [ENTITY_TYPES.CASH_MOVEMENT]: {
    icon: Banknote,
    color: 'text-emerald-600 hover:text-emerald-800',
    bgColor: 'bg-emerald-50',
    label: 'Caisse',
    route: '/cash'
  }
};

// Styles de base pour tous les liens d'entité
const baseLinkStyles = "inline-flex items-center gap-1.5 font-medium hover:underline cursor-pointer transition-colors";

// Composant de lien générique
const EntityLink = ({ 
  type, 
  id, 
  children, 
  showIcon = true,
  showArrow = false,
  variant = 'inline', // 'inline' | 'badge' | 'card'
  className = '',
  onClick
}) => {
  const navigate = useNavigate();
  const config = entityConfig[type];

  if (!config) {
    console.warn(`Unknown entity type: ${type}`);
    return <span>{children}</span>;
  }

  const Icon = config.icon;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick(e);
    }
    // Navigation directe vers la page avec l'ID
    navigate(`${config.route}?id=${id}`);
  };

  if (variant === 'badge') {
    return (
      <button
        onClick={handleClick}
        className={cn(
          "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all",
          config.bgColor,
          config.color,
          "hover:ring-2 hover:ring-offset-1 hover:ring-current/20",
          className
        )}
        data-testid={`entity-link-${type}-${id}`}
      >
        {showIcon && <Icon className="w-3 h-3" />}
        {children}
        {showArrow && <ChevronRight className="w-3 h-3" />}
      </button>
    );
  }

  if (variant === 'card') {
    return (
      <button
        onClick={handleClick}
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg border transition-all w-full text-left",
          "hover:bg-muted/50 hover:border-primary/30",
          className
        )}
        data-testid={`entity-link-${type}-${id}`}
      >
        <div className={cn("p-2 rounded-lg", config.bgColor)}>
          <Icon className={cn("w-4 h-4", config.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={cn("font-medium truncate", config.color)}>{children}</p>
          <p className="text-xs text-muted-foreground">{config.label}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </button>
    );
  }

  // Variante inline par défaut
  return (
    <button
      onClick={handleClick}
      className={cn(baseLinkStyles, config.color, className)}
      data-testid={`entity-link-${type}-${id}`}
    >
      {showIcon && <Icon className="w-3.5 h-3.5" />}
      {children}
      {showArrow && <ChevronRight className="w-3 h-3" />}
    </button>
  );
};

// Composants spécialisés pour chaque type d'entité
export const GuestLink = ({ guest, showIcon = true, variant = 'inline', className = '' }) => {
  if (!guest) return <span className="text-muted-foreground">-</span>;
  
  const displayName = guest.first_name && guest.last_name 
    ? `${guest.first_name} ${guest.last_name}`
    : guest.name || guest.email || 'Client inconnu';

  return (
    <EntityLink 
      type={ENTITY_TYPES.GUEST} 
      id={guest.id} 
      showIcon={showIcon}
      variant={variant}
      className={className}
    >
      {displayName}
    </EntityLink>
  );
};

export const ReservationLink = ({ reservation, showIcon = true, variant = 'inline', className = '' }) => {
  if (!reservation) return <span className="text-muted-foreground">-</span>;
  
  const displayText = reservation.reservation_number || `#${reservation.id}`;

  return (
    <EntityLink 
      type={ENTITY_TYPES.RESERVATION} 
      id={reservation.id}
      showIcon={showIcon}
      variant={variant}
      className={className}
    >
      {displayText}
    </EntityLink>
  );
};

export const SaleLink = ({ sale, showIcon = true, variant = 'inline', className = '' }) => {
  if (!sale) return <span className="text-muted-foreground">-</span>;
  
  const displayText = sale.sale_number || `#${sale.id}`;

  return (
    <EntityLink 
      type={ENTITY_TYPES.SALE} 
      id={sale.id}
      showIcon={showIcon}
      variant={variant}
      className={className}
    >
      {displayText}
    </EntityLink>
  );
};

export const ProductLink = ({ product, showIcon = true, variant = 'inline', className = '' }) => {
  if (!product) return <span className="text-muted-foreground">-</span>;

  return (
    <EntityLink 
      type={ENTITY_TYPES.PRODUCT} 
      id={product.id}
      showIcon={showIcon}
      variant={variant}
      className={className}
    >
      {product.name}
    </EntityLink>
  );
};

export const PaymentLink = ({ payment, showIcon = true, variant = 'inline', className = '' }) => {
  if (!payment) return <span className="text-muted-foreground">-</span>;
  
  const displayText = `${payment.payment_method || 'Paiement'} - ${formatCurrency(payment.amount)}`;

  return (
    <EntityLink 
      type={ENTITY_TYPES.PAYMENT} 
      id={payment.id}
      showIcon={showIcon}
      variant={variant}
      className={className}
    >
      {displayText}
    </EntityLink>
  );
};

export const EmployeeLink = ({ employee, showIcon = true, variant = 'inline', className = '' }) => {
  if (!employee) return <span className="text-muted-foreground">-</span>;
  
  const displayName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Employé';

  return (
    <EntityLink 
      type={ENTITY_TYPES.EMPLOYEE} 
      id={employee.id}
      showIcon={showIcon}
      variant={variant}
      className={className}
    >
      {displayName}
    </EntityLink>
  );
};

export const ExpenseLink = ({ expense, showIcon = true, variant = 'inline', className = '' }) => {
  if (!expense) return <span className="text-muted-foreground">-</span>;

  return (
    <EntityLink 
      type={ENTITY_TYPES.EXPENSE} 
      id={expense.id}
      showIcon={showIcon}
      variant={variant}
      className={className}
    >
      {expense.description || `Dépense #${expense.id}`}
    </EntityLink>
  );
};

export const SupplierLink = ({ supplier, showIcon = true, variant = 'inline', className = '' }) => {
  if (!supplier) return <span className="text-muted-foreground">-</span>;

  return (
    <EntityLink 
      type={ENTITY_TYPES.SUPPLIER} 
      id={supplier.id}
      showIcon={showIcon}
      variant={variant}
      className={className}
    >
      {supplier.name || `Fournisseur #${supplier.id}`}
    </EntityLink>
  );
};

export const CashMovementLink = ({ movement, showIcon = true, variant = 'inline', className = '' }) => {
  if (!movement) return <span className="text-muted-foreground">-</span>;
  
  const prefix = movement.type === 'IN' ? '+' : '-';
  const displayText = `${prefix}${formatCurrency(movement.amount)}`;

  return (
    <EntityLink 
      type={ENTITY_TYPES.CASH_MOVEMENT} 
      id={movement.id}
      showIcon={showIcon}
      variant={variant}
      className={className}
    >
      {displayText}
    </EntityLink>
  );
};

// Export du composant générique
export { EntityLink };
export default EntityLink;
